import { Box, SxProps, Theme } from '@mui/material'
import { ChangeEvent, ReactNode } from 'react'
import { TOKENS, MONO } from './SchedulerDesign'

// Re-exported so existing importers (RulesWorkbench.view) keep working while
// SchedulerDesign remains the single source of truth for tokens.
export { TOKENS, MONO }

/**
 * Shared timeline + token primitives for the Rules & Constraints workbench
 * (the redesign documented in
 * `Scheduler/design_handoff_rules_and_constraints/`). These mirror the
 * prototype's `ConstraintsShared.jsx` primitives but are rebuilt on MUI `Box`
 * with the SupplyVue theme rather than the prototype's inline CSS variables.
 *
 * The 168-hour "week timeline" is hand-positioned with absolutely-positioned
 * boxes (the prototype's approach, explicitly permitted by the handoff) since
 * the footprints shown are derived illustrations rather than persisted data.
 */

export const TOTAL_HOURS = 168
export const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

/** mode → colour for temperature segments (band labels are the °C value). */
export const MODE_COLOR: Record<string, string> = {
  '132': TOKENS.ganttProduction,
  '135': TOKENS.brandTeal,
  '138': TOKENS.ganttClean,
  '141': '#7a5fb0',
  co: TOKENS.ganttChangeover,
}

export const pct = (h: number) => (h / TOTAL_HOURS) * 100

// ---------------------------------------------------------------------------
// Pure compute helpers — breach computations are pure functions of the draft
// and recompute on every edit (handoff Change 2 → "State").
// ---------------------------------------------------------------------------

export type Window = [number, number]

/** concurrent-line count over a membership set → compressed bands. */
export const concurrency = (
  activity: Record<string, Window[]>,
  members: string[]
): { start: number; end: number; count: number }[] => {
  const counts = new Array(TOTAL_HOURS).fill(0)
  members.forEach((l) =>
    (activity[l] || []).forEach(([s, e]) => {
      for (let h = s; h < e; h++) counts[h]++
    })
  )
  const out: { start: number; end: number; count: number }[] = []
  let cur: { start: number; end: number; count: number } | null = null
  for (let h = 0; h <= TOTAL_HOURS; h++) {
    const c = counts[h] || 0
    if (!cur || cur.count !== c) {
      if (cur) out.push(cur)
      cur = { start: h, end: h + 1, count: c }
    } else {
      cur.end = h + 1
    }
  }
  return out.filter((b) => b.count > 0)
}

export const overrunHours = (
  strip: { start: number; end: number; count: number }[],
  max: number
) =>
  strip.filter((b) => b.count > max).reduce((a, b) => a + (b.end - b.start), 0)

/** deterministic free-basket pool sawtooth (lines consume, cycles release). */
export const makePool = (
  opening: number,
  cap: number,
  consumeHi: number,
  releaseEvery: number,
  releaseAmt: number
): { h: number; free: number }[] => {
  const pts: { h: number; free: number }[] = []
  let free = opening
  for (let h = 0; h <= TOTAL_HOURS; h += 4) {
    free -= h > 8 && h < 150 ? consumeHi : consumeHi * 0.25
    if (releaseEvery > 0 && h % releaseEvery === 0 && h > 0) free += releaseAmt
    free = Math.max(0, Math.min(cap, free))
    pts.push({ h, free: Number(free.toFixed(1)) })
  }
  return pts
}

export const poolMin = (pts: { h: number; free: number }[]) =>
  pts.length ? Math.min(...pts.map((p) => p.free)) : 0

/** cycle-lock windows derived from cycle length + cadence. */
export const cyclesFor = (cycleHours: number, every = 22): Window[] => {
  const out: Window[] = []
  for (let h = 10; h < TOTAL_HOURS - cycleHours; h += every)
    out.push([h, h + cycleHours])
  return out
}

/** deterministic hourly-usage profile for a resource, compressed to bands. */
export const resUsage = (
  id: string,
  amount: number
): { start: number; end: number; used: number }[] => {
  let s = 0
  for (let i = 0; i < id.length; i++)
    s = (s * 31 + id.charCodeAt(i)) & 0x7fffffff
  const bands: { start: number; end: number; used: number }[] = []
  let h = 0
  while (h < TOTAL_HOURS) {
    const len = 10 + (s % 18)
    s = (s * 7 + 13) & 0x7fffffff
    const load = 0.25 + (s % 90) / 100
    s = (s * 7 + 5) & 0x7fffffff
    bands.push({
      start: h,
      end: Math.min(TOTAL_HOURS, h + len),
      used: Math.round(load * amount),
    })
    h += len
  }
  return bands
}

/**
 * Synthesise a per-line activity footprint inside a crew's claimed periods.
 * The persisted SOR has no per-line activity data, so (as in the prototype)
 * this is a deterministic illustration derived from the line id and the
 * period bounds — it reacts to period/line edits so the breach ribbon is live.
 */
export const synthActivity = (
  lines: string[],
  periods: Window[]
): Record<string, Window[]> => {
  const out: Record<string, Window[]> = {}
  lines.forEach((l, li) => {
    let seed = 0
    for (let i = 0; i < l.length; i++)
      seed = (seed * 31 + l.charCodeAt(i)) & 0x7fffffff
    const windows: Window[] = []
    periods.forEach(([ps, pe]) => {
      const span = Math.max(0, pe - ps)
      if (span <= 0) return
      const offset = ps + (seed % Math.max(1, Math.floor(span / 4)))
      const dur = Math.max(
        4,
        Math.floor(span * (0.4 + ((seed >> li) % 40) / 100))
      )
      windows.push([offset, Math.min(pe, offset + dur)])
    })
    out[l] = windows
  })
  return out
}

export type TempSeg = [number, number, string]

/** Synthesise per-line temperature mode segments + conflict windows. */
export const synthTempSegments = (
  lines: string[],
  mode: string
): { segs: Record<string, TempSeg[]>; conflicts: Window[] } => {
  const base = mode.replace(/[^0-9]/g, '') || '132'
  const segs: Record<string, TempSeg[]> = {}
  const conflicts: Window[] = []
  lines.forEach((l, li) => {
    let seed = 0
    for (let i = 0; i < l.length; i++)
      seed = (seed * 31 + l.charCodeAt(i)) & 0x7fffffff
    // most lines hold the group mode; an occasional line diverges late-week,
    // producing a short conflict window the planner must resolve.
    if (li > 0 && seed % 3 === 0) {
      const switchAt = 88 + (seed % 24)
      segs[l] = [
        [0, switchAt, base],
        [switchAt, switchAt + 4, 'co'],
        [switchAt + 4, TOTAL_HOURS, '138'],
      ]
      conflicts.push([switchAt, switchAt + 4])
    } else {
      segs[l] = [[0, TOTAL_HOURS, base]]
    }
  })
  return { segs, conflicts }
}

// ---- string (de)serialisation for the SOR `lines` / `periods` fields --------

export const parseLines = (s: string | undefined | null): string[] =>
  (s ? String(s) : '')
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean)

export const joinLines = (arr: string[]): string => arr.join(', ')

/** parse `(2,12),(18,26)` → [[2,12],[18,26]] */
export const parsePeriods = (s: string | undefined | null): Window[] => {
  if (!s) return []
  const out: Window[] = []
  // Decimals are accepted: the engine parses "(57.5,60)" as a window, so a
  // stored fractional window must survive a read-modify-write here. An
  // integer-only pattern skipped it, and the next save dropped it silently.
  const re = /\(\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)\s*\)/g
  let m: RegExpExecArray | null
  while ((m = re.exec(String(s))))
    out.push([parseFloat(m[1]), parseFloat(m[2])])
  return out
}

export const joinPeriods = (arr: Window[]): string =>
  arr.map(([a, b]) => `(${a},${b})`).join(',')

/** Small tabular-numeral hour input, clamped to [0, TOTAL_HOURS]. Used by the
 * period/window editors (crew active periods, downtime windows). */
export const PeriodNum = ({
  value,
  onChange,
}: {
  value: number
  onChange: (v: number) => void
}) => (
  <Box
    component="input"
    value={value}
    onChange={(e: ChangeEvent<HTMLInputElement>) => {
      const v = parseInt(e.target.value.replace(/\D/g, ''), 10)
      onChange(isNaN(v) ? 0 : Math.min(TOTAL_HOURS, v))
    }}
    sx={{
      width: 34,
      border: `1px solid ${TOKENS.line300}`,
      borderRadius: '3px',
      background: '#fff',
      textAlign: 'center',
      fontFamily: MONO,
      fontSize: 12.5,
      fontWeight: 700,
      color: TOKENS.ink900,
      p: '3px 0',
      outline: 'none',
    }}
  />
)

// ---------------------------------------------------------------------------
// Primitive components
// ---------------------------------------------------------------------------

export const DayHeader = ({ pad = 0 }: { pad?: number }) => (
  <Box sx={{ display: 'flex', pl: `${pad}px`, mb: '3px' }}>
    {DAY_LABELS.map((d) => (
      <Box
        key={d}
        sx={{
          flex: 1,
          textAlign: 'center',
          fontSize: 9.5,
          fontWeight: 700,
          letterSpacing: '0.04em',
          color: TOKENS.ink400,
        }}
      >
        {d}
      </Box>
    ))}
  </Box>
)

export const WeekTrack = ({
  height = 22,
  children,
  bg = TOKENS.ganttIdle,
  radius = 3,
  sx,
}: {
  height?: number
  children?: ReactNode
  bg?: string
  radius?: number
  sx?: SxProps<Theme>
}) => (
  <Box
    sx={{
      position: 'relative',
      height,
      background: bg,
      border: `1px solid ${TOKENS.line200}`,
      borderRadius: `${radius}px`,
      overflow: 'hidden',
      ...sx,
    }}
  >
    {Array.from({ length: TOTAL_HOURS / 6 - 1 }, (_, i) => {
      const h = (i + 1) * 6
      const day = h % 24 === 0
      return (
        <Box
          key={h}
          sx={{
            position: 'absolute',
            left: `${pct(h)}%`,
            top: 0,
            bottom: 0,
            width: '1px',
            background: day ? 'rgba(14,27,34,0.16)' : 'rgba(14,27,34,0.05)',
          }}
        />
      )
    })}
    {children}
  </Box>
)

export const Band = ({
  start,
  end,
  color,
  label,
  hatch,
  title,
  dim,
  top = 2,
  bottom = 2,
  radius = 2,
  textColor = '#fff',
}: {
  start: number
  end: number
  color: string
  label?: string
  hatch?: boolean
  title?: string
  dim?: boolean
  top?: number
  bottom?: number
  radius?: number
  textColor?: string
}) => (
  <Box
    title={title}
    sx={{
      position: 'absolute',
      left: `${pct(start)}%`,
      width: `${pct(end - start)}%`,
      top: `${top}px`,
      bottom: `${bottom}px`,
      background: hatch
        ? `repeating-linear-gradient(45deg, ${color} 0 5px, rgba(255,255,255,.35) 5px 10px)`
        : color,
      opacity: dim ? 0.4 : 1,
      borderRadius: `${radius}px`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
      minWidth: 0,
    }}
  >
    {label && (
      <Box
        component="span"
        sx={{
          fontSize: 8.5,
          fontWeight: 700,
          color: textColor,
          fontFamily: MONO,
          whiteSpace: 'nowrap',
          px: '3px',
        }}
      >
        {label}
      </Box>
    )}
  </Box>
)

export const ConflictMark = ({
  start,
  end,
}: {
  start: number
  end: number
}) => (
  <Box
    title="Temperature conflict"
    sx={{
      position: 'absolute',
      left: `${pct(start)}%`,
      width: `${pct(end - start)}%`,
      top: '-2px',
      bottom: '-2px',
      background: 'rgba(196,69,54,0.18)',
      border: `1px solid ${TOKENS.ganttDowntime}`,
      borderRadius: '2px',
      zIndex: 3,
    }}
  />
)

type Tone = 'neutral' | 'blue' | 'teal' | 'amber'
const TONE_STYLES: Record<Tone, { bg: string; fg: string; bd: string }> = {
  neutral: { bg: TOKENS.surface50, fg: TOKENS.ink700, bd: TOKENS.line300 },
  blue: { bg: TOKENS.blue50, fg: TOKENS.blue600, bd: TOKENS.brandBlue },
  teal: { bg: TOKENS.tealTint, fg: TOKENS.brandTeal, bd: TOKENS.teal300 },
  amber: {
    bg: 'rgba(224,146,47,0.12)',
    fg: TOKENS.amberFg,
    bd: 'rgba(224,146,47,0.6)',
  },
}

export const LineToken = ({
  name,
  tone = 'neutral',
  removable,
  onRemove,
  small,
}: {
  name: string
  tone?: Tone
  removable?: boolean
  onRemove?: () => void
  small?: boolean
}) => {
  const t = TONE_STYLES[tone]
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        background: t.bg,
        color: t.fg,
        border: `1px solid ${t.bd}`,
        borderRadius: '3px',
        px: small ? '6px' : '8px',
        py: small ? '1px' : '2px',
        fontSize: small ? 10.5 : 11.5,
        fontWeight: 600,
        fontFamily: MONO,
        whiteSpace: 'nowrap',
      }}
    >
      {name}
      {removable && (
        <Box
          component="span"
          onClick={onRemove}
          sx={{
            cursor: 'pointer',
            opacity: 0.7,
            fontSize: 14,
            lineHeight: 1,
            '&:hover': { opacity: 1 },
          }}
        >
          ×
        </Box>
      )}
    </Box>
  )
}

export const CapMeter = ({
  used,
  cap,
  label,
  width = 120,
  danger,
}: {
  used: number
  cap: number
  label?: string
  width?: number
  danger?: boolean
}) => {
  const r = cap > 0 ? Math.min(1, used / cap) : 0
  const col =
    danger || r > 0.92
      ? TOKENS.ganttDowntime
      : r > 0.75
        ? TOKENS.ganttClean
        : TOKENS.ganttProduction
  return (
    <Box sx={{ width }}>
      {label && (
        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', mb: '3px' }}
        >
          <Box
            component="span"
            sx={{ fontSize: 9.5, color: TOKENS.ink400, fontWeight: 600 }}
          >
            {label}
          </Box>
          <Box
            component="span"
            sx={{
              fontSize: 9.5,
              fontFamily: MONO,
              color: TOKENS.ink600,
              fontWeight: 600,
            }}
          >
            {used}/{cap}
          </Box>
        </Box>
      )}
      <Box
        sx={{
          height: 6,
          background: TOKENS.ganttIdle,
          borderRadius: '3px',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            width: `${r * 100}%`,
            height: '100%',
            background: col,
            borderRadius: '3px',
          }}
        />
      </Box>
    </Box>
  )
}

export const PoolSpark = ({
  data,
  cap,
  height = 64,
  floor = 0,
}: {
  data: { h: number; free: number }[]
  cap: number
  height?: number
  floor?: number
}) => {
  const w = 100
  const h = height
  const max = cap || 1
  const pts = data.map((d) => [pct(d.h), h - (d.free / max) * h])
  const path = pts
    .map((p, i) => (i ? 'L' : 'M') + p[0].toFixed(2) + ' ' + p[1].toFixed(2))
    .join(' ')
  const area = path + ` L100 ${h} L0 ${h} Z`
  const floorY = h - (floor / max) * h - 0.5
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      style={{ width: '100%', height, display: 'block' }}
    >
      <defs>
        <linearGradient id="poolg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="rgba(0,130,179,0.28)" />
          <stop offset="1" stopColor="rgba(0,130,179,0.02)" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#poolg)" />
      <path
        d={path}
        fill="none"
        stroke={TOKENS.ganttProduction}
        strokeWidth="1.2"
        vectorEffect="non-scaling-stroke"
      />
      <line
        x1="0"
        x2="100"
        y1={floorY}
        y2={floorY}
        stroke={TOKENS.ganttDowntime}
        strokeWidth="1"
        strokeDasharray="3 2"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  )
}

export type WorkbenchTab = 'sync' | 'crew' | 'ster' | 'res'

export const RuleTabs = ({
  tabs,
  active,
  onPick,
}: {
  tabs: { id: WorkbenchTab; label: string; icon: ReactNode; count: number }[]
  active: WorkbenchTab
  onPick: (id: WorkbenchTab) => void
}) => (
  <Box sx={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
    {tabs.map((t) => {
      const on = t.id === active
      return (
        <Box
          component="button"
          key={t.id}
          onClick={() => onPick(t.id)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            border: 'none',
            background: on ? TOKENS.brandTeal : 'transparent',
            color: on ? '#fff' : TOKENS.ink500,
            px: '11px',
            py: '6px',
            borderRadius: '3px',
            cursor: 'pointer',
            fontSize: 12,
            fontWeight: 600,
            fontFamily: 'inherit',
            '& svg': { fontSize: 15, color: on ? '#fff' : TOKENS.ink400 },
          }}
        >
          {t.icon}
          {t.label}
          <Box
            component="span"
            sx={{
              fontFamily: MONO,
              fontSize: 10,
              fontWeight: 700,
              background: on ? 'rgba(255,255,255,0.2)' : TOKENS.line200,
              color: on ? '#fff' : TOKENS.ink500,
              borderRadius: '9px',
              px: '6px',
            }}
          >
            {t.count}
          </Box>
        </Box>
      )
    })}
  </Box>
)

export const Stat = ({
  label,
  value,
  unit,
  tone,
}: {
  label: string
  value: ReactNode
  unit?: string
  tone?: string
}) => (
  <Box>
    <Box
      sx={{
        fontSize: 9,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: TOKENS.ink400,
        mb: '2px',
      }}
    >
      {label}
    </Box>
    <Box
      sx={{
        fontFamily: MONO,
        fontSize: 16,
        fontWeight: 600,
        color: tone || TOKENS.ink900,
        lineHeight: 1,
      }}
    >
      {value}
      {unit && (
        <Box
          component="span"
          sx={{ fontSize: 10, color: TOKENS.ink400, ml: '2px' }}
        >
          {unit}
        </Box>
      )}
    </Box>
  </Box>
)

export const Stepper = ({
  label,
  value,
  onDec,
  onInc,
}: {
  label?: string
  value: ReactNode
  onDec: () => void
  onInc: () => void
}) => (
  <Box>
    {label && (
      <Box
        sx={{
          fontSize: 9.5,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: TOKENS.ink400,
          mb: '3px',
        }}
      >
        {label}
      </Box>
    )}
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        height: 28,
        border: `1px solid ${TOKENS.line300}`,
        borderRadius: '3px',
        background: '#fff',
        overflow: 'hidden',
      }}
    >
      <Box
        component="button"
        onClick={onDec}
        sx={{
          width: 26,
          height: '100%',
          border: 'none',
          borderRight: `1px solid ${TOKENS.line200}`,
          background: TOKENS.surface50,
          cursor: 'pointer',
          color: TOKENS.ink500,
          fontSize: 15,
        }}
      >
        −
      </Box>
      <Box
        component="span"
        sx={{
          width: 40,
          textAlign: 'center',
          fontFamily: MONO,
          fontSize: 13,
          fontWeight: 700,
          color: TOKENS.ink900,
        }}
      >
        {value}
      </Box>
      <Box
        component="button"
        onClick={onInc}
        sx={{
          width: 26,
          height: '100%',
          border: 'none',
          borderLeft: `1px solid ${TOKENS.line200}`,
          background: TOKENS.surface50,
          cursor: 'pointer',
          color: TOKENS.ink500,
          fontSize: 15,
        }}
      >
        +
      </Box>
    </Box>
  </Box>
)
